import * as THREE from "three";
import { loadCurveFromJSON } from "@/three/CurveMethods";
import { LAYERS } from "@/three/constants";
import { BIRD_POSITION_TYPES, TEST_PLACEMENT } from "@/helpers/GameConstants";

const MARKER_TIMEOUT = 1000;
const MARKER_COLOR_DARK = 0xff0000;
const MARKER_COLOR_LIGHT = 0xffffff;
const MARKER_Y = 92;
const SOUND_REF_DISTANCE = 0.5;
const SOUND_REF_DISTANCE_FLYING = 5;
const RANDOM_POSITION_RANGE = 6;
const DEFAULT_SCALE = 4; // This is the default scale for birds
const RANDOM_SCALE_RANGE = 0.25 * DEFAULT_SCALE; // Random scale range for birds, 25% of the default scale
const BIRD_SPEED = 0.03; // Controls the speed of the birds in the air. Initially, this was 0.35, but it was too fast.
const DEFAULT_SONG_FILE_INDEX = 1; // generally, the second song file is the preferred one

export default class Bird {
  /**
   * Creates a new Bird object
   * @param {*} gltf loaded gltf file
   * @param {*} config bird species config data
   * @param {*} marker marker for bird on mini map
   * @param {*} songFiles bird song file paths
   * @param {*} flightpath flightpath for flying birds
   */
  constructor(gltf, config, marker, positionType, scale, flightpath = null) {
    this.model = gltf.scene;
    this.songFiles = config.songFiles;
    this.flightpath = flightpath;
    this.marker = marker;
    this.config = config;
    this.positionType = positionType;
    this.flyingSpeed =
      config.flyingSpeed !== undefined ? config.flyingSpeed : 1;

    // Scale down the model
    // TODO: make this dynamic when we have more models
    this.model.scale.multiplyScalar(scale);
    // Set size used for photo grading
    this.size = this.getSize();

    this.mixer = new THREE.AnimationMixer(this.model);
    // Consider storing animation timeScales in json if speeds are inconsistent across models
    this.mixer.timeScale = 1.5;

    this.animations = gltf.animations.map((clip) =>
      this.mixer.clipAction(clip)
    );
    if (this.animations.length > 0) {
      // Retrieve specific animations by name
      const animationHeadTilt = this.animations.find((action) =>
        action.getClip().name.includes("headTilt")
      );
      const animationWingFlap = this.animations.find((action) =>
        action.getClip().name.includes("wingFlap")
      );
      // Select animation based on positionType
      if (this.positionType === BIRD_POSITION_TYPES.FLYING) {
        this.animation = animationWingFlap;
      } else {
        // Default to headTilt
        this.animation = animationHeadTilt;
      }

      if (this.animation) {
        this.animation.loop = THREE.LoopRepeat;
        // Randomize the start time of the animation so that the birds don't all move in unison
        this.animation.time = Math.random() * this.animation.getClip().duration;
        this.animation.play();
      } else {
        console.log(this.config, "Selected animation not found");
      }
    } else {
      console.log("No animation found");
    }

    this.curveTime = 0;

    // TODO: castShadow(gltf.scene);

    // Call update once to set initial position
    this.update(0);
  }

  /**
   * Factory method for creating a list of Bird objects from config data
   *
   * @param {*} config data for bird species
   * @param {*} loadModel function to load model
   * @returns Promise<Bird|null>
   */
  static async createInstances(config, loadModel) {
    const instances = [];
    const gltfFiles = [];
    let flightpath;

    const filename = config.flying
      ? config.filename + "-inFlight"
      : config.filename;
    const flightpathFilename = config.filename + "-flightPath";

    // Determine counts for different bird states
    const flyingCount = config.flying ? 1 : 0;
    // No ground position type birds unless otherwise specified in config
    const defaultGroundCount = 0;
    const groundCount =
      config.groundCount === undefined
        ? defaultGroundCount
        : config.groundCount;
    // Handle missing count data in config json
    const swimmingCount = config.swimmingCount ? config.swimmingCount : 0;
    const perchedCount = config.perchedCount ? config.perchedCount : 0;
    const totalBirdCount = TEST_PLACEMENT
      ? 1
      : swimmingCount + perchedCount + groundCount + flyingCount;

    const promises = [];

    const marker = new THREE.Mesh(
      new THREE.SphereGeometry(6),
      new THREE.MeshStandardMaterial({ color: 0xff0000 })
    );
    marker.layers.set(LAYERS.MINI_MAP_ONLY);
    marker.visible = false;

    // Load glb files for each bird instance, marking failed loads with null
    for (let i = 0; i < totalBirdCount; i++) {
      // Load gltf file
      promises.push(
        loadModel(`models/birds/${filename}.glb`)
          .then((gltfFile) => {
            gltfFiles.push(gltfFile);
          })
          .catch(() => {
            console.warn(
              `Model for '${config.commonName}' could not be loaded and will be skipped.`
            );
            gltfFiles.push(null); // Push null to maintain order, indicating a skipped file
          })
      );
    }

    // Load the flight path for flying birds, using a default if specific path fails
    if (config.flying) {
      await loadCurveFromJSON(`flightpaths/${flightpathFilename}.json`)
        .then((curveData) => {
          flightpath = curveData.curve;
          // Set marker position here if necessary
          marker.position.set(curveData.center.x, MARKER_Y, curveData.center.z);
          // Promise debugging log
          // console.log(
          //   `Flight path for '${config.commonName}' loaded successfully.`
          // );
        })
        .catch((error) => {
          console.warn(
            `Flight path for '${config.commonName}' could not be loaded. Loading default flight path. Error: ${error}`
          );
          loadCurveFromJSON(`flightpaths/default-flightPath.json`)
            .then((defaultCurveData) => {
              flightpath = defaultCurveData.curve;
              // Set marker position here if necessary
              marker.position.set(
                defaultCurveData.center.x,
                MARKER_Y,
                defaultCurveData.center.z
              );
            })
            .catch((error) => {
              console.error(`Failed to load default flight path. ${error}`);
              flightpath = null;
            });
        });
    }

    await Promise.all(promises);

    // Process all promises and then create Bird instances from successful loads
    const validGltfFiles = gltfFiles.filter((file) => file !== null);
    // Promise debugging log
    // console.log(
    //   `.then() for '${config.commonName} Promise.all() entered. validFiles: ${validGltfFiles}.`
    // );
    // Return new Bird instances if gltf was loaded successfully
    const instantiatePosition = (positionType, count) => {
      for (let i = 0; i < count; i++) {
        if (validGltfFiles.length === 0) break; // Stop if no more valid models
        const gltfFile = validGltfFiles.pop(); // Use a valid GLTF file
        const scaleMod = (Math.random() - 0.5) * RANDOM_SCALE_RANGE;
        const scale = DEFAULT_SCALE + scaleMod;
        instances.push(
          new Bird(
            gltfFile,
            config,
            marker,
            positionType,
            scale,
            flightpath // Handle null flightpath in Bird constructor if necessary
          )
        );
      }
    };
    // Instantiate birds for each position type
    if (TEST_PLACEMENT) {
      instantiatePosition(BIRD_POSITION_TYPES.GROUND, 1);
    } else {
      // instantiatePosition(BIRD_POSITION_TYPES.GROUND, groundCount);
      instantiatePosition(BIRD_POSITION_TYPES.FLYING, flyingCount);
      instantiatePosition(BIRD_POSITION_TYPES.PERCHED, perchedCount);
      // instantiatePosition(BIRD_POSITION_TYPES.SWIMMING, swimmingCount);
    }
    return instances;
  }

  /**
   *
   * @param {*} position Vector3 position to copy
   */
  setPosition(position, rotation) {
    // if (this.positionType === BIRD_POSITION_TYPES.FLYING) {
    //   return;
    // }
    this.model.position.copy(position);
    this.model.rotation.y = rotation.z;
    this.marker.position.copy(position);
    this.marker.position.y = MARKER_Y;
  }

  /**
   * Sets the position of the bird, offset and rotated randomly
   * @param {*} position Vector3 position to copy
   */
  setPositionRandomized(position) {
    if (this.positionType === BIRD_POSITION_TYPES.FLYING) {
      return;
    }
    // Randomize position
    this.model.position.copy(position);
    this.model.position.x +=
      Math.random() * RANDOM_POSITION_RANGE - RANDOM_POSITION_RANGE / 2;
    this.model.position.z +=
      Math.random() * RANDOM_POSITION_RANGE - RANDOM_POSITION_RANGE / 2;
    // randomize rotation
    this.model.rotation.y = Math.random() * Math.PI * 2;
    // Set marker position
    this.marker.position.copy(this.model.position);
    this.marker.position.y = MARKER_Y;
  }
  /** CHAT GPT USED HERE
   * Generates Poisson-distributed random numbers.
   * @param {number} lambda The average number of events in an interval.
   * @returns {number} A random number following Poisson distribution.
   */
  poissonRandomNumber(lambda) {
    // Calculate the threshold L to decide when to stop generating random numbers.
    let L = Math.exp(-lambda);
    let k = 0; // Counts the number of occurrences (the result).
    let p = 1; // Product of random numbers, used to simulate random events.

    // Generate random numbers until the product p is less than threshold L.
    do {
      k++;
      p *= Math.random(); // Update p with another random number.
    } while (p > L);

    // Return the count of occurrences minus one, as the loop runs one time extra.
    return k - 1;
  }

  /**
   * Initializes the bird's audio
   * @param {*} audioListener AudioListener object attached to camera
   * @param {*} numBirdsOfSameSpecies Number of birds of the same species
   * @param {*} birdIndex Index of the bird in the list of birds of the same species
   */
  initAudio(audioListener, numBirdsOfSameSpecies, birdIndex) {
    // create the PositionalAudio object (passing in the listener)
    this.sound = new THREE.PositionalAudio(audioListener);
    this.sound.setLoop(true);

    const setSoundBuffer = (buffer) => {
      this.sound.setBuffer(buffer);
      this.sound.setRefDistance(
        this.positionType === BIRD_POSITION_TYPES.FLYING
          ? SOUND_REF_DISTANCE_FLYING
          : SOUND_REF_DISTANCE
      );

      // Select unique start time for each bird
      this.sound.currentTime =
        birdIndex * (this.sound.buffer.duration / (numBirdsOfSameSpecies - 1));
      this.sound.play();

      this.model.add(this.sound);
    };

    // load a sound and set it as the PositionalAudio object's buffer
    const audioLoader = new THREE.AudioLoader();
    const index =
      this.config.songFileIndex !== undefined ||
      this.songFiles.length < DEFAULT_SONG_FILE_INDEX + 1
        ? this.config.songFileIndex
        : DEFAULT_SONG_FILE_INDEX;
    audioLoader.load(
      require(`@/assets/audio/${this.songFiles[index]}`),
      setSoundBuffer.bind(this)
    );
  }

  pauseAudio() {
    if (this.sound) {
      this.sound.pause();
    }
  }

  resumeAudio() {
    if (this.sound) {
      this.sound.play();
    }
  }

  endAudio() {
    if (this.sound) {
      this.sound.stop();
      this.model.remove(this.sound);
      this.sound = null;
    }
  }

  /**
   * Updates Bird at a single frame
   * @param {*} delta Clock delta
   */
  update(delta) {
    this?.mixer.update(delta);
    // Update position along curve
    this.curveTime += delta * BIRD_SPEED * this.flyingSpeed; // Controls the speed of the movement
    if (this.curveTime > 1) this.curveTime %= 1; // Reset t to loop

    // Update aerial position of bird if it's flying
    if (this.positionType === BIRD_POSITION_TYPES.FLYING && this.flightpath) {
      // Get the point at curveTime
      const point = this.flightpath.getPoint(this.curveTime);

      // Set the model's position to the point
      this.model.position.set(point.x, point.y, point.z);
      // Update marker on the minimap if the bird is flying
      this.marker.position.set(point.x, MARKER_Y, point.z);

      // Align the model to the curve's tangent
      const tangent = this.flightpath.getTangent(this.curveTime).normalize();
      // Create a vector pointing in the direction the model is currently facing (positive z-axis for Blender)
      const axis = new THREE.Vector3(0, 0, 1);
      this.model.quaternion.setFromUnitVectors(axis, tangent);
    }
  }

  getSize() {
    // Create a bounding sphere
    const boundingSphere = new THREE.Sphere();
    const boundingBox = new THREE.Box3().setFromObject(this.model);
    boundingBox.getBoundingSphere(boundingSphere);

    // The diameter of the sphere is an estimate of the bird's size
    return boundingSphere.radius * 2;
  }

  animateMarker() {
    if (this.marker.material.color.getHex() === 0xff0000) {
      this.marker.material.color.setHex(MARKER_COLOR_LIGHT);
    } else {
      this.marker.material.color.setHex(MARKER_COLOR_DARK);
    }
    return setTimeout(() => {
      this.animateMarker();
    }, MARKER_TIMEOUT);
  }

  toggleMarker() {
    this.marker.visible = !this.marker.visible;
    if (this.marker.visible) {
      // Start animating marker
      this.markerTimeoutId = this.animateMarker();
    } else {
      clearTimeout(this.markerTimeoutId);
    }
  }
}
